import { ReactNode, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PageLoader from 'src/components/PageLoader';
import { GET_QUOTE, GetQuoteResponse } from 'src/configs/APISchemas/GetQuote';
import { PaymentPlan } from 'src/configs/APISchemas/QuoteSchema';
import { ContactSupportId } from 'src/constants/constants';
import { logToBackend } from 'src/helpers/BackendLogger';
import { GraphQLRequest, QueryTypes } from 'src/helpers/GraphQLRequest';
import {
  ResponseErrorType,
  showError,
  showErrorAndCode,
} from 'src/helpers/error.helper';
import { useMount } from 'src/helpers/hooks/useMount';
import { getQuoteMutationSerializer } from 'src/pages/payment-msi/PaymentMsiV2/serializers';
import { updatePurchasePolicyAdditionalData } from 'src/redux/actions/purchasePolicy';
import {
  getPurchasePolicyAdditionalData,
  getPurchasePolicyInput,
} from 'src/redux/selectors/purchasePolicy';

interface PaymentPlanLoaderProps {
  children: ReactNode;
  onPaymentPlansLoaded: (paymentPlans: PaymentPlan[]) => void;
}

export function PaymentPlansLoader({
  children,
  onPaymentPlansLoaded,
}: PaymentPlanLoaderProps) {
  const [loadingStatus, setLoadingStatus] = useState<'pending' | 'resolved'>(
    'pending',
  );

  const dispatch = useDispatch();
  const purchasePolicyInput = useSelector(getPurchasePolicyInput);
  const purchasePolicyAdditionalData = useSelector(
    getPurchasePolicyAdditionalData,
  );

  const updateQuote = useCallback(async () => {
    try {
      const getQuoteSerializedMutation = getQuoteMutationSerializer({
        purchasePolicyInput,
        purchasePolicyAdditionalData,
      });

      const mutation = GET_QUOTE(getQuoteSerializedMutation);

      const response = await GraphQLRequest<GetQuoteResponse>(mutation, {
        typeOfQuery: QueryTypes.GetQuoteMutation,
      });

      if (!response.getQuote) {
        throw Error('Application Not Created');
      }

      if ('errors' in response.getQuote) {
        return showError(response.getQuote.errors[0]?.message);
      }
      const responseData = response.getQuote;

      dispatch(
        updatePurchasePolicyAdditionalData({
          companyProductCd: responseData.companyProductCd,
          paymentPlans: responseData.paymentPlans,
          id: responseData.id,
        }),
      );

      return responseData.paymentPlans;
    } catch (error: unknown) {
      showErrorAndCode(
        error as ResponseErrorType,
        ContactSupportId.paymentMSIUpdateQuote,
      );
      await logToBackend((error as ResponseErrorType)?.toString());
    }
  }, [dispatch, purchasePolicyAdditionalData, purchasePolicyInput]);

  useMount(() => {
    updateQuote()
      .then((paymentPlans) => {
        if (!paymentPlans) {
          const errorMessage = `We are having some trouble, try to refresh the page. If the problem persists, please contact support for help. id: ${ContactSupportId.paymentMSIUpdateQuote}`;

          toast(errorMessage, {
            type: 'error',
          });

          return;
        }

        onPaymentPlansLoaded(paymentPlans);
      })
      .finally(() => {
        setLoadingStatus('resolved');
      });
  });

  if (loadingStatus === 'pending') {
    return <PageLoader />;
  }

  return <>{children}</>;
}
